import { TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { ZoomIn, ZoomOut, RotateCcw, Pin, X, ChevronUp, ChevronDown } from 'lucide-react';
import type { ResultsBearingContent, ResultsPanelState } from './useResultsPanelState';

type ResultsPanelTabBarProps = {
  state: ResultsPanelState;
  content: ResultsBearingContent | undefined;
  tabsBarHeight: number;
  /** Shortcut hint appended to the collapse toggle's title (QE passes 'Ctrl+J'). */
  collapseShortcutHint?: string;
  /** Amber "results are stale" notice (VQE-only); QE passes nothing. */
  staleNotice?: string;
  /** T13 — the Map trigger renders only while the active result set has ≥1 spatial column. */
  hasSpatialColumns?: boolean;
  /**
   * 0-based indexes of the rows the last "Export All to CSV" could not read.
   * Non-empty ⇒ the conditional "Skipped rows (N)" trigger renders.
   */
  skippedRows?: number[] | null;
};

export function ResultsPanelTabBar({
  state,
  content,
  tabsBarHeight,
  collapseShortcutHint,
  staleNotice,
  hasSpatialColumns,
  skippedRows,
}: ResultsPanelTabBarProps) {
  const hintSuffix = collapseShortcutHint ? ` (${collapseShortcutHint})` : '';
  return (
    <div
      className="flex-shrink-0 flex items-center px-2 bg-background border-t border-border"
      style={{ height: tabsBarHeight }}
    >
      <TabsList className="rounded-md bg-muted/30 h-6 flex items-stretch text-xs">
        <TabsTrigger
          value="results"
          className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm"
          data-testid="results-tab-trigger"
        >
          Results
        </TabsTrigger>
        <TabsTrigger
          value="messages"
          className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm"
          data-testid="messages-tab-trigger"
        >
          Messages
        </TabsTrigger>
        <TabsTrigger
          value="plan"
          className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm"
          data-testid="plan-tab-trigger"
        >
          Plan
        </TabsTrigger>
        <TabsTrigger
          value="chart"
          className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm"
          data-testid="chart-tab-trigger"
        >
          Chart
        </TabsTrigger>
        {hasSpatialColumns && (
          <TabsTrigger
            value="map"
            className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm"
            data-testid="map-tab-trigger"
          >
            Map
          </TabsTrigger>
        )}
        {skippedRows != null && skippedRows.length > 0 && (
          <TabsTrigger
            value="skipped-rows"
            // Amber tint: this trigger IS the missing-data notice once the toast
            // is dismissed — same warning hue as the panel's banner.
            className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm text-amber-700 dark:text-amber-400 [&[data-state=active]]:text-amber-700 dark:[&[data-state=active]]:text-amber-400"
            data-testid="skipped-rows-tab-trigger"
          >
            Skipped rows ({skippedRows.length.toLocaleString()})
          </TabsTrigger>
        )}
        {content?.pinnedResultSets?.map((pinned) => {
          const resultCount = pinned.resultSets.length;
          const totalRows = pinned.resultSets.reduce((sum, rs) => sum + (rs.rowCount ?? 0), 0);
          let summary: string;
          if (resultCount === 0) summary = 'Output';
          else if (resultCount > 1) summary = `${resultCount} results, ${totalRows.toLocaleString()} rows`;
          else summary = `${(pinned.resultSets[0]?.rowCount ?? 0).toLocaleString()} rows`;
          return (
            <TabsTrigger
              key={pinned.id}
              value={`pinned-${pinned.id}`}
              className="px-3 py-0 rounded-sm text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm gap-1"
              data-testid={`pinned-tab-${pinned.id}`}
            >
              <Pin className="h-3 w-3" />
              Pinned
              <span className="text-muted-foreground">
                ({summary})
              </span>
              <button
                type="button"
                className="ml-1 p-0.5 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                data-testid={`pinned-tab-close-${pinned.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  state.handleClosePinnedTab(pinned.id);
                }}
              >
                <X className="h-3 w-3" />
              </button>
            </TabsTrigger>
          );
        })}
      </TabsList>
      <div className="flex items-center gap-0.5 ml-auto">
        {staleNotice && (
          <span
            data-testid="vqe-results-stale"
            className="text-xs text-warning mr-1"
          >
            {staleNotice}
          </span>
        )}
        <button
          type="button"
          className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
          onClick={() => state.setCollapsed((prev) => !prev)}
          title={state.collapsed ? `Show results pane${hintSuffix}` : `Hide results pane${hintSuffix}`}
          data-testid="results-pane-toggle"
        >
          {state.collapsed ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {state.zoomLevel !== 1 && (
          <button
            type="button"
            className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
            onClick={() => state.setZoomLevel(1)}
            title={`Reset zoom (${Math.round(state.zoomLevel * 100)}%)`}
            data-testid="results-zoom-reset"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        )}
        <button
          type="button"
          className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
          onClick={() => state.setZoomLevel((z) => Math.max(0.5, +(z - 0.1).toFixed(1)))}
          title="Zoom out"
          data-testid="results-zoom-out"
        >
          <ZoomOut className="h-3 w-3" />
        </button>
        <button
          type="button"
          className="p-1 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/60"
          onClick={() => state.setZoomLevel((z) => Math.min(2, +(z + 0.1).toFixed(1)))}
          title="Zoom in"
          data-testid="results-zoom-in"
        >
          <ZoomIn className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
